import { useEffect, useRef, useState, type ReactNode } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { GoogleIcon, MicrosoftIcon } from "../assets/icons";
import {
  useLoginWithGoogleMutation,
  useLoginWithMicrosoftMutation,
} from "../features/auth/authApi";
import { useDisconnectMailboxMutation, useConnectGoogleMailboxMutation } from "../features/users/usersApi";
import { MailboxType } from "../features/users/usersTypes";
import DeleteConfirmation from "./modal/DeleteConfirmation";
import GoogleMailboxConnectModal from "./modal/GoogleMailboxConnectModal";
import { GMAIL_SEND_SCOPE, hasGmailSendScope } from "../utils/signatureHelpers";
const msClientKey = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
const localUrl = import.meta.env.VITE_LOCAL_URL;
const GOOGLE_LOGIN_SCOPES = [
  "https://www.googleapis.com/auth/userinfo.profile",
  "https://www.googleapis.com/auth/userinfo.email",
];
const GOOGLE_PROFILE_SCOPES = [GMAIL_SEND_SCOPE];

type MailboxConnectButtonsProps = {
  mailboxType?: MailboxType;
  mailboxScope?: string;
  variant: "login" | "profile";
  helpText?: string;
  onConnectSuccess?: () => void;
};

const MailboxConnectButtons = ({
  mailboxType,
  mailboxScope,
  variant,
  helpText,
  onConnectSuccess,
}: MailboxConnectButtonsProps) => {
  const navigate = useNavigate();
  const [loginWithGoogle] = useLoginWithGoogleMutation();
  const [loginWithMicrosoft] = useLoginWithMicrosoftMutation();
  const [disconnectMailbox, { isLoading: isDisconnecting }] =
    useDisconnectMailboxMutation();
  const [connectGoogleMailbox] = useConnectGoogleMailboxMutation();
  const msCallbackHandled = useRef(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showGoogleConnectModal, setShowGoogleConnectModal] = useState(false);

  const resolvedMailboxType = mailboxType ?? MailboxType.NOT_CONNECTED;

  const isGoogleConnected =
    resolvedMailboxType === MailboxType.GOOGLE &&
    hasGmailSendScope(mailboxScope);
  const isMicrosoftConnected = resolvedMailboxType === MailboxType.MICROSOFT;
  const isMailboxConnected = isGoogleConnected || isMicrosoftConnected;
  const isGooglePermissionMissing =
    variant === "profile" &&
    resolvedMailboxType === MailboxType.GOOGLE &&
    !hasGmailSendScope(mailboxScope);

  const persistTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
  };

  const handleAuthSuccess = (
    accessToken: string,
    refreshToken: string,
    successMessage: string
  ) => {
    persistTokens(accessToken, refreshToken);
    setIsConnecting(false);
    if (variant === "login") {
      navigate("/");
      return;
    }
    toast.success(successMessage);
    onConnectSuccess?.();
    navigate("/profile", { replace: true });
  };

  const handleGoogleSuccess = async (tokenResponse: {
    scope: string;
    code: string;
  }) => {
    if (variant === "profile" && !hasGmailSendScope(tokenResponse.scope)) {
      setIsConnecting(false);
      toast.error(
        'Send email permission was not granted. Connect again and click Allow for "Send email on your behalf".',
        { autoClose: 8000 }
      );
      return;
    }

    setIsConnecting(true);

    if (variant === "profile") {
      try {
        await connectGoogleMailbox({
          code: tokenResponse.code,
          scope: tokenResponse.scope,
        }).unwrap();
        setIsConnecting(false);
        toast.success("Google mailbox connected successfully");
        onConnectSuccess?.();
        navigate("/profile", { replace: true });
      } catch (e: unknown) {
        setIsConnecting(false);
        const err = e as { data?: { message?: string } };
        toast.error(err?.data?.message ?? "Failed to connect Google mailbox");
      }
      return;
    }

    try {
      const loginResponse = await loginWithGoogle({
        scope: tokenResponse.scope,
        code: tokenResponse.code,
      }).unwrap();

      const accessToken = loginResponse?.data?.accessToken;
      const refreshToken = loginResponse?.data?.refreshToken;

      if (accessToken && refreshToken) {
        handleAuthSuccess(
          accessToken,
          refreshToken,
          "Google mailbox connected successfully"
        );
      } else {
        setIsConnecting(false);
        toast.error("Failed to connect Google mailbox");
      }
    } catch (e: unknown) {
      setIsConnecting(false);
      const err = e as { data?: { message?: string } };
      toast.error(err?.data?.message ?? "Failed to connect Google mailbox");
    }
  };

  const handleMicrosoftCode = async (code: string) => {
    setIsConnecting(true);
    try {
      const loginResponse = await loginWithMicrosoft({
        code,
        redirectUri: microsoftRedirectUri,
      }).unwrap();
      const accessToken = loginResponse?.data?.accessToken;
      const refreshToken = loginResponse?.data?.refreshToken;

      if (accessToken && refreshToken) {
        handleAuthSuccess(
          accessToken,
          refreshToken,
          "Outlook mailbox connected successfully"
        );
      } else {
        setIsConnecting(false);
        toast.error("Failed to connect Outlook mailbox");
      }
    } catch (e: unknown) {
      setIsConnecting(false);
      const err = e as { data?: { message?: string } };
      toast.error(err?.data?.message ?? "Failed to connect Outlook mailbox");
      if (variant === "profile") {
        navigate("/profile", { replace: true });
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMailbox().unwrap();
      toast.success("Mailbox disconnected");
      onConnectSuccess?.();
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      toast.error(err?.data?.message ?? "Failed to disconnect mailbox");
    }
  };

  const handleDisconnectConfirm = () => {
    setShowDisconnectConfirm(false);
    void handleDisconnect();
  };

  const handleProfileGoogleConnect = () => {
    setShowGoogleConnectModal(true);
  };

  const handleGoogleConnectContinue = () => {
    setShowGoogleConnectModal(false);
    googleLogin();
  };

  const googleLoginScopes =
    variant === "profile" ? GOOGLE_PROFILE_SCOPES : GOOGLE_LOGIN_SCOPES;

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      void handleGoogleSuccess({
        scope: tokenResponse.scope,
        code: tokenResponse.code,
      });
    },
    onError: () => toast.error("Google sign-in was cancelled or failed"),
    flow: "auth-code",
    scope: googleLoginScopes.join(" "),
  });

  const microsoftRedirectPath =
    variant === "profile" ? "/profile" : "/login";
  const microsoftRedirectUri = `${localUrl}${microsoftRedirectPath}`;

  const msLoginUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${msClientKey}&response_type=code&redirect_uri=${encodeURIComponent(microsoftRedirectUri)}&response_mode=query&scope=offline_access%20user.read%20mail.send&state=12345`;

  useEffect(() => {
    if (variant !== "profile") return;

    const code = new URLSearchParams(window.location.search).get("code");
    if (!code || msCallbackHandled.current) return;

    msCallbackHandled.current = true;
    void handleMicrosoftCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const profileStatusLabel = isGoogleConnected
    ? "Google connected"
    : isMicrosoftConnected
      ? "Outlook connected"
      : isGooglePermissionMissing
        ? "Send permission missing"
        : "Not connected";

  const profileInteractionLocked = isConnecting || isDisconnecting;

  const renderProfileProviderButton = (
    provider: "google" | "microsoft",
    label: string,
    icon: ReactNode,
    onConnect?: () => void,
    href?: string
  ) => {
    const isActive =
      (provider === "google" && isGoogleConnected) ||
      (provider === "microsoft" && isMicrosoftConnected);
    const isInactiveWhenConnected = isMailboxConnected && !isActive;
    const isDisabled =
      profileInteractionLocked || isMailboxConnected;

    const className = [
      "profile-mailbox__provider-btn",
      isActive ? "profile-mailbox__provider-btn--active" : "",
      isInactiveWhenConnected ? "profile-mailbox__provider-btn--disabled" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const content = (
      <>
        <span className="profile-mailbox__provider-icon">{icon}</span>
        <span className="profile-mailbox__provider-label">{label}</span>
      </>
    );

    if (isDisabled || !href) {
      return (
        <button
          key={provider}
          type="button"
          className={className}
          disabled={isDisabled}
          onClick={!isDisabled ? onConnect : undefined}
        >
          {content}
        </button>
      );
    }

    return (
      <a key={provider} className={className} href={href}>
        {content}
      </a>
    );
  };

  if (variant === "profile") {
    return (
      <>
        <div className="profile-mailbox">
        <div className="profile-mailbox__header">
          <div className="profile-mailbox__content">
            <h3 className="profile-mailbox__title">Email mailbox</h3>
            <p className="profile-mailbox__description">
              Connect Google or Outlook to send gifts from your mailbox. Only one
              provider can be connected at a time.
            </p>
          </div>
          <span
            className={`mailbox-status-badge ${isMailboxConnected ? "mailbox-status-badge--connected" : ""}`}
          >
            {profileStatusLabel}
          </span>
        </div>

        {isGooglePermissionMissing && (
          <p className="profile-mailbox__scope-warning" role="alert">
            Google is linked but send-email permission is missing. Connect Google
            again and click Allow.
          </p>
        )}

        <div className="profile-mailbox__providers">
          {renderProfileProviderButton(
            "google",
            "Google",
            <GoogleIcon />,
            handleProfileGoogleConnect,
            undefined
          )}
          {renderProfileProviderButton(
            "microsoft",
            "Outlook",
            <MicrosoftIcon />,
            undefined,
            profileInteractionLocked || isMailboxConnected
              ? undefined
              : msLoginUrl
          )}
        </div>

        {(isMailboxConnected || isConnecting || isDisconnecting) && (
          <div className="profile-mailbox__footer">
            <p className="profile-mailbox__status mb-0">
              {isDisconnecting
                ? "Disconnecting mailbox…"
                : isConnecting
                  ? "Connecting mailbox…"
                  : "Your mailbox is ready to send gifts."}
            </p>
            {isMailboxConnected && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => setShowDisconnectConfirm(true)}
                disabled={isDisconnecting || isConnecting}
              >
                {isDisconnecting ? "Disconnecting…" : "Disconnect"}
              </button>
            )}
          </div>
        )}
        </div>

        <DeleteConfirmation
          show={showDisconnectConfirm}
          onHide={() => setShowDisconnectConfirm(false)}
          title="Disconnect mailbox"
          description="Are you sure you want to disconnect your mailbox? You won't be able to send gifts via email until you connect again."
          onConfirmation={handleDisconnectConfirm}
        />

        <GoogleMailboxConnectModal
          show={showGoogleConnectModal}
          onHide={() => setShowGoogleConnectModal(false)}
          onContinue={handleGoogleConnectContinue}
        />
      </>
    );
  }

  return (
    <>
      <div className="d-flex flex-column gap-2 mb-2">
        <button
          type="button"
          onClick={() => googleLogin()}
          className="social-button"
        >
          <GoogleIcon /> Continue with Google
        </button>
        <a className="social-button" href={msLoginUrl}>
          <MicrosoftIcon /> Continue with Microsoft
        </a>
      </div>

      {helpText && (
        <div className="d-flex align-items-center my-2">
          <div
            className="flex-grow border-top"
            style={{ borderColor: "rgba(0, 0, 0, 0.2)" }}
          />
          <span
            className="mx-2 small text-nowrap"
            style={{ fontSize: "11px", color: "#1a202c" }}
          >
            {helpText}
          </span>
          <div
            className="flex-grow border-top"
            style={{ borderColor: "rgba(0, 0, 0, 0.2)" }}
          />
        </div>
      )}
    </>
  );
};

export default MailboxConnectButtons;
